import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
} from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { PlanId } from "components/common/boundary/PlanId";
import { LoadedContent } from "components/common/loading/loadedContent";
import { SummaryStat } from "components/common/summaryStat";
import { InventoryTable } from "components/plan/table/inventory/inventoryTable";
import { usePlan } from "contexts/plan";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { usePeriodIndex } from "hooks/periodIndex";
import { useTenant } from "hooks/settings";
import { mapLoaded } from "models/loaded";
import { useCallback, useState } from "react";
import {
  NullableDatetime,
  useDeployPlanMutation,
} from "src/store/api/generatedApi";
import { useNotifyStatus } from "contexts/status";

interface Props {
  toPreviousState: () => void;
  toNextState: () => void;
  planId: PlanId;
  doClose: () => void;
}

export const Approve = ({ toPreviousState, toNextState, planId }: Props) => {
  const { t } = useTenantTranslation();
  const apiRef = useGridApiRef();

  return (
    <>
      <DialogContent>
        <PlanSummary planId={planId} />
        <Box sx={{ py: 1, minHeight: 0 }}>
          <InventoryTable planId={planId} apiRef={apiRef} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={toPreviousState}>
          {t("back")}
        </Button>
        <DeployButton planId={planId} toNextState={toNextState} />
      </DialogActions>
    </>
  );
};

const PlanSummary = ({ planId }: { planId: PlanId }) => {
  const { plan } = usePlan(planId);
  const periodTab = usePeriodIndex();
  const { t } = useTenantTranslation();
  return (
    <LoadedContent
      data={mapLoaded(plan, (p) => {
        const periodSummary = p.summary.period_summary.find(
          (period_summary) => period_summary.period === periodTab
        );
        if (periodSummary) {
          return periodSummary;
        } else {
          return null;
        }
      })}
    >
      {(summary) => {
        return (
          <Stack direction="row" spacing={4} sx={{ pb: 2, px: 1 }}>
            <SummaryStat
              label="tappedMass"
              value={summary === null ? null : summary.display_produced_mass}
              decimalPlaces={0}
              units="mass"
            />
            <SummaryStat
              label="tappedPrice"
              value={summary === null ? null : summary.display_cost_per_tonne}
              decimalPlaces={2}
              units="specific_cost"
            />
            <SummaryStat
              label={t("heats")}
              value={summary?.num_heats ?? null}
              decimalPlaces={0}
              units={null}
            />
            <SummaryStat
              label="scrap"
              value={summary === null ? null : summary.scrap_mass}
              decimalPlaces={0}
              units="mass"
            />
            <SummaryStat
              label="yield"
              value={summary === null ? null : summary.display_yield_percent}
              decimalPlaces={1}
              units="yield"
              hideDivider
            />
          </Stack>
        );
      }}
    </LoadedContent>
  );
};

const DeployButton = ({
  planId,
  toNextState,
}: {
  planId: PlanId;
  toNextState: () => void;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const [deployPlan] = useDeployPlanMutation();
  const { plan } = usePlan(planId);
  const periodTab = usePeriodIndex();
  const [deploying, setDeploying] = useState(false);
  const notifyStatus = useNotifyStatus();

  const handleDeploy = useCallback(async () => {
    const now = dayjs();
    if (plan.status === "success") {
      const periodSummary = plan.data.summary.period_summary.find(
        (period_summary) => period_summary.period === periodTab
      );
      if (periodSummary) {
        const ensureTimezoneAware = (timestamp: NullableDatetime) => {
          if (!timestamp) return now.toISOString();
          return dayjs(timestamp).toISOString();
        };
        setDeploying(true);

        await deployPlan({
          tenantName,
          planId,
          deployParameters: {
            process: "none",
            language: "EN",
            start_at: ensureTimezoneAware(periodSummary.start_timestamp),
            end_at: ensureTimezoneAware(periodSummary.end_timestamp),
            deployment_type: "purchasing_plan",
          },
        })
          .unwrap()
          .then(() => {
            toNextState();
          })
          .catch(() => {
            notifyStatus({
              type: "error",
              text: t("errorLoadingData"),
            });
          })
          .finally(() => {
            setDeploying(false);
          });
      }
    }
  }, [
    plan,
    deployPlan,
    planId,
    periodTab,
    tenantName,
    notifyStatus,
    t,
    toNextState,
  ]);

  return (
    <Button
      variant="contained"
      loading={plan.status !== "success" || deploying}
      onClick={() => void handleDeploy()}
    >
      {t("deploy")}
    </Button>
  );
};
